import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { Download, Upload } from "lucide-react";
import api from "../services/api";

// The offline route to e-invoices / e-way bills, for distributors who generate on the government portals themselves:
// download the selected invoices as the portal's bulk-upload JSON, upload it there, then import the result file the
// portal gives back so the IRN / e-way bill numbers land on the invoices.

const BASE = "/api/v1/sales-invoices/gst-bulk/portal-json";

const KINDS = {
  E_INVOICE: { label: "E-Invoice JSON", portal: "e-invoice portal (Bulk Upload)" },
  E_WAY_BILL: { label: "E-Way Bill JSON", portal: "e-way bill portal (Generate Bulk)" },
};

const saveJson = (fileName, content) => {
  const url = URL.createObjectURL(new Blob([JSON.stringify(content, null, 2)], { type: "application/json" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const Modal = ({ title, onClose, children }) => (
  <div className="modal-overlay" role="presentation" onClick={(e) => e.target === e.currentTarget && onClose()}>
    <div className="modal-box" style={{ maxWidth: "860px" }}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-semibold">{title}</h3>
        <button className="close-btn" onClick={onClose}>
          Close
        </button>
      </div>
      {children}
      <div className="modal-actions">
        <button type="button" className="primary" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  </div>
);

/** Buttons for the selection bar: download the selected invoices as a bulk-upload file for either portal. */
export const PortalJsonDownloadButtons = ({ selectedInvoices }) => {
  const [busyKind, setBusyKind] = useState("");
  const [result, setResult] = useState(null);

  const download = async (kind) => {
    setBusyKind(kind);
    try {
      const data = await api.post(`${BASE}/export`, { invoiceIds: selectedInvoices.map((invoice) => invoice.id), kind });
      if (data.json) saveJson(data.fileName, data.json);
      if (data.exported > 0 && data.skipped.length === 0) {
        toast.success(`${data.fileName} downloaded with ${data.exported} invoice(s). Upload it on the ${KINDS[kind].portal}.`);
      } else {
        // something was left out (or nothing could be exported): show exactly which invoices and why
        setResult(data);
      }
    } catch (error) {
      toast.error(error.message || "Failed to prepare the JSON file");
    } finally {
      setBusyKind("");
    }
  };

  return (
    <>
      {Object.entries(KINDS).map(([kind, info]) => (
        <button
          key={kind}
          className="secondary"
          onClick={() => download(kind)}
          disabled={Boolean(busyKind) || selectedInvoices.length === 0}
          title={`Download the selected invoices as a JSON file for the ${info.portal}`}
          style={{ display: "inline-flex", alignItems: "center", gap: "5px" }}
        >
          <Download size={13} /> {busyKind === kind ? "Preparing..." : `${info.label} (${selectedInvoices.length})`}
        </button>
      ))}

      {result ? (
        <Modal title={KINDS[result.kind].label} onClose={() => setResult(null)}>
          <div className="page-subtitle" style={{ marginBottom: "10px" }}>
            {result.exported > 0
              ? `${result.fileName} was downloaded with ${result.exported} invoice(s). Upload it on the ${KINDS[result.kind].portal}. `
              : "No file was downloaded — none of the selected invoices could be included. "}
            {result.skipped.length} invoice(s) were left out:
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th style={{ textAlign: "left" }}>Why it was left out</th>
                </tr>
              </thead>
              <tbody>
                {result.skipped.map((skip) => (
                  <tr key={skip.invoiceId}>
                    <td style={{ whiteSpace: "nowrap" }}>{skip.invoiceNumber || `#${skip.invoiceId}`}</td>
                    <td style={{ textAlign: "left", fontSize: "12.5px" }}>{skip.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      ) : null}
    </>
  );
};

const OUTCOMES = {
  UPDATED: { label: "Updated", badge: "badge-green" },
  UNCHANGED: { label: "Already recorded", badge: "badge-gray" },
  SKIPPED: { label: "Skipped", badge: "badge-amber" },
  ERROR: { label: "Not updated", badge: "badge-red" },
};

/** Button for the toolbar: import the result file downloaded from a portal and record IRNs / e-way bill numbers on the invoices. */
export const PortalJsonImportButton = ({ onImported }) => {
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [onlyProblems, setOnlyProblems] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const data = await api.uploadFile(`${BASE}/import`, file);
      setOnlyProblems(false);
      setResult(data);
      if (data.updated > 0) onImported?.();
    } catch (error) {
      toast.error(error.message || "Failed to import the JSON file");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const rows = result ? result.rows.filter((row) => !onlyProblems || row.outcome === "ERROR" || row.outcome === "SKIPPED") : [];

  return (
    <>
      <input ref={fileInputRef} type="file" accept=".json,application/json" style={{ display: "none" }} onChange={handleFile} />
      <button
        type="button"
        className="secondary"
        disabled={importing}
        onClick={() => fileInputRef.current?.click()}
        title="Import the JSON downloaded from the e-invoice / e-way bill portal after bulk generation: IRN, acknowledgement and e-way bill numbers are saved on the invoices with the same invoice numbers"
        style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}
      >
        <Upload size={15} />
        {importing ? "Importing..." : "Import GST Portal JSON"}
      </button>

      {result ? (
        <Modal title="GST Portal JSON Import" onClose={() => setResult(null)}>
          <div className="filters-bar" style={{ alignItems: "center" }}>
            <span style={{ fontSize: "13px" }}>
              {result.records} record(s) in the file: <strong>{result.updated}</strong> updated, {result.unchanged} already recorded,{" "}
              {result.skipped} skipped, <strong style={{ color: result.errors ? "var(--color-danger)" : undefined }}>{result.errors}</strong> not
              updated
            </span>
            {result.errors + result.skipped > 0 ? (
              <label style={{ display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "13px" }}>
                <input type="checkbox" checked={onlyProblems} onChange={(e) => setOnlyProblems(e.target.checked)} />
                Show only problems
              </label>
            ) : null}
          </div>
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Result</th>
                  <th>IRN</th>
                  <th>E-Way Bill</th>
                  <th style={{ textAlign: "left" }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={`${row.documentNumber}-${index}`}>
                    <td style={{ whiteSpace: "nowrap" }}>{row.documentNumber || "-"}</td>
                    <td>
                      <span className={`badge ${OUTCOMES[row.outcome]?.badge || "badge-gray"}`}>{OUTCOMES[row.outcome]?.label || row.outcome}</span>
                    </td>
                    <td title={row.irn || ""} style={{ fontSize: "12px" }}>
                      {row.irn ? `${row.irn.slice(0, 10)}…` : "-"}
                    </td>
                    <td style={{ fontSize: "12px" }}>{row.ewayBillNumber || "-"}</td>
                    <td style={{ textAlign: "left", fontSize: "12.5px", color: row.outcome === "ERROR" ? "var(--color-danger)" : undefined }}>
                      {row.message}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Modal>
      ) : null}
    </>
  );
};
